import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@akuna/ui";
import type { DailyRevenuePoint } from "@/hooks/useRevenueReport";
import { exportToExcel } from "@/lib/utils/export";

interface RevenueLineChartProps {
  data: DailyRevenuePoint[];
}

function formatCurrencyShort(value: number) {
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}k`;
  return `R$ ${value.toFixed(0)}`;
}

export function RevenueLineChart({ data = [] }: RevenueLineChartProps) {
  const { t } = useTranslation("reports");

  function handleExport() {
    exportToExcel({
      rows: data.map((d) => ({
        [t("date")]: d.date,
        [t("realized")]: d.realized,
        [t("previousPeriod")]: d.previous,
      })),
      sheetName: t("revenueChartTitle"),
      fileName: "receita-por-periodo.xlsx",
    });
  }

  const hasPrevious = data.some((d) => d.previous > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t("revenueChartTitle")}</CardTitle>
            <CardDescription>{t("revenueChartDesc")}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={data.length === 0}>
            <Download className="h-4 w-4 mr-1" />
            {t("export")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
            {t("emptyBreakdown")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={formatCurrencyShort}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value: number, name: string) => [formatCurrencyShort(value), name]}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend iconType="line" iconSize={12} wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="realized"
                name={t("realized")}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              {hasPrevious && (
                <Line
                  type="monotone"
                  dataKey="previous"
                  name={t("previousPeriod")}
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
